<template>
  <div class="md-render-fallback" :data-reason="reason">
    <div class="fallback-header">
      <div class="fallback-icon">
        <i class="el-icon-warning-outline"></i>
      </div>
      <div class="fallback-text">
        <div class="fallback-title">
          {{ $t('workbench.componentFallbackTitle') }}
          <span v-if="typeLabel" class="fallback-badge">{{ typeLabel }}</span>
        </div>
        <div class="fallback-desc">{{ $t('workbench.componentFallbackDesc') }}</div>
      </div>
    </div>

    <div v-if="hasRaw" class="fallback-actions">
      <button type="button" class="fallback-btn" @click="showRaw = !showRaw">
        <i :class="showRaw ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        {{ showRaw ? $t('workbench.componentFallbackHideRaw') : $t('workbench.componentFallbackViewRaw') }}
      </button>
      <button type="button" class="fallback-btn" @click="handleCopy">
        <i class="el-icon-document-copy"></i>
        {{ $t('workbench.componentFallbackCopy') }}
      </button>
      <button
        v-if="canFix"
        type="button"
        class="fallback-btn"
        :disabled="fixing"
        @click="handleFixWithAI"
      >
        <i :class="fixing ? 'el-icon-loading' : 'el-icon-magic-stick'"></i>
        {{ fixing ? $t('workbench.componentFallbackFixing') : $t('workbench.componentFallbackFixWithAI') }}
      </button>
    </div>

    <pre v-if="hasRaw && showRaw" class="fallback-raw">{{ raw }}</pre>
  </div>
</template>

<script>
import { COMPONENT_TYPE_NAMES, reparseComponentJson } from '../md-renderer'

export default {
  name: 'MdRenderFallback',
  props: {
    // 失败原因编码，仅用于排查（data-reason + 控制台日志），不面向用户展示
    reason: { type: String, default: 'render_error' },
    // 组件名，如 chart；用于展示中文名标签
    componentName: { type: String, default: '' },
    // 原始卡片内容，供用户查看/复制
    raw: { type: String, default: '' }
  },
  data() {
    return {
      showRaw: false,
      fixing: false
    }
  },
  computed: {
    typeLabel() {
      if (!this.componentName) return ''
      return COMPONENT_TYPE_NAMES[this.componentName] || this.componentName
    },
    hasRaw() {
      return !!(this.raw && this.raw.trim())
    },
    // 修复能力依赖 jsonCardChecker 注册的全局校验修复器（未引入则不展示修复入口）
    canFix() {
      return this.hasRaw && typeof window !== 'undefined' && typeof window.__JSON_CARD_CHECKER__ === 'function'
    }
  },
  created() {
    console.warn('[MdRenderFallback] 组件降级展示', { reason: this.reason, componentName: this.componentName })
  },
  methods: {
    async handleCopy() {
      if (!this.raw) return
      const text = `${this.$t('workbench.componentFallbackFixPrompt')}\n${this.raw}`
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$message && this.$message.success(this.$t('workbench.componentFallbackCopied'))
      } catch (e) {
        this.$message && this.$message.error(this.$t('workbench.componentFallbackCopyFailed'))
      }
    },
    async handleFixWithAI() {
      if (!this.canFix || this.fixing) return
      this.fixing = true
      let result = null
      try {
        // 走 jsonCardChecker 的校验修复服务，不再把内容转发给大模型
        result = await window.__JSON_CARD_CHECKER__(this.raw)
      } catch (e) {
        result = null
      }
      this.fixing = false
      // 同时确认修复后的 JSON 能被本地解析成可渲染组件，避免只上报“成功”却仍展示兜底
      const payload = result && result.valid && !result.fallback && result.cardJson
        ? reparseComponentJson(result.cardJson)
        : null
      if (payload) {
        // 修复成功：把修复后的 JSON 交给父组件替换原始内容并重新渲染
        this.$emit('fixed', { ...result, payload })
        this.$message && this.$message.success(this.$t('workbench.componentFallbackFixed'))
      } else {
        this.$message && this.$message.error(this.$t('workbench.componentFallbackFixFailed'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.md-render-fallback {
  margin: 8px 0;
  padding: 12px 14px;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  background: #fbfbfd;
}

.fallback-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.fallback-icon {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff6e6;
  color: #ed990e;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fallback-text {
  flex: 1;
  min-width: 0;
}

.fallback-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #1f2329;
  line-height: 20px;
}

.fallback-badge {
  padding: 0 6px;
  border-radius: 4px;
  background: #f2f3f5;
  color: #646a73;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.fallback-desc {
  margin-top: 2px;
  font-size: 12px;
  color: #8a919f;
  line-height: 18px;
}

.fallback-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  padding-left: 38px;
}

.fallback-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #3370ff;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;

  &:hover {
    color: #245bdb;
  }

  &:disabled {
    color: #a8abb2;
    cursor: not-allowed;
  }
}

.fallback-raw {
  margin: 8px 0 0;
  padding: 10px 12px;
  max-height: 220px;
  overflow: auto;
  border-radius: 8px;
  background: #f5f6f7;
  color: #646a73;
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
